import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RefreshCw, RotateCcw } from 'lucide-react';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { ErrorState } from '../../../../../components/ui/ErrorState';
import { ShopifyRunSummary } from '../../../../../components/warehouses/ShopifyRunSummary';
import { ShopifyScopesNotice } from '../../../../../components/warehouses/ShopifyScopesNotice';
import { reauthorizeShopify } from '../../../../../features/ecommerces/ecommercesThunks';
import { selectIsRunning } from '../../../../../features/feedback/feedbackSlice';
import { stockSyncChannelSelected } from '../../../../../features/stockSync/stockSyncSlice';
import { fetchStockSync, startCatalogSync } from '../../../../../features/stockSync/stockSyncThunks';
import {
  selectChannelOptions,
  selectIsRunActive,
  selectRunOf,
} from '../../../../../features/stockSync/stockSyncSelectors';
import { RUN_KIND, RUN_STATUS } from '../../../../../features/stockSync/stockSyncConstants';
import { catalogSyncBlocker, scopesProblemOf } from '../../../../../features/stockSync/stockSyncUtils';
import { formatQuantity } from '../../../../../utils/inventoryLabels';

const PRODUCTS_PATH = '/warehouse/products';

/**
 * Bringing a Shopify store's SKUs into the catalog: products created for the
 * variants Ninja does not have, an empty EAN or weight filled in, and each
 * variant linked to its product. Nothing changes in Shopify.
 *
 * The run goes on in the server: the dialog can close and the page keeps
 * watching it (`stockSyncListeners`), then a toast says what it left. The
 * store is the seller's own: when it lacks the permissions, the seller
 * authorizes Ninja again from here and comes back to this page.
 *
 * @param {object} props
 * @param {() => void} props.onClose
 */
const ShopifySkuSyncDialog = ({ onClose }) => {
  const dispatch = useDispatch();
  const channelOptions = useSelector(selectChannelOptions);
  const { selectedId, detail, detailLoading, detailError } = useSelector((state) => state.stockSync);
  const report = useSelector(selectRunOf(RUN_KIND.CATALOG));
  const running = useSelector(selectIsRunActive(RUN_KIND.CATALOG));
  const starting = useSelector(selectIsRunning(startCatalogSync));
  const reauthorizing = useSelector(selectIsRunning(reauthorizeShopify));
  const [error, setError] = useState(null);
  const [reauthorizeError, setReauthorizeError] = useState(null);

  useEffect(() => {
    if (selectedId) dispatch(fetchStockSync({ settingId: selectedId }));
  }, [dispatch, selectedId]);

  const handleSync = async () => {
    setError(null);
    const action = await dispatch(startCatalogSync({ settingId: selectedId }));
    if (startCatalogSync.rejected.match(action)) setError(action.payload);
  };

  const handleReauthorize = async () => {
    setReauthorizeError(null);
    const action = await dispatch(reauthorizeShopify({ integrationId: selectedId, returnTo: PRODUCTS_PATH }));
    if (reauthorizeShopify.rejected.match(action)) setReauthorizeError(action.payload);
  };

  const blocker = catalogSyncBlocker(detail);
  const failed = report?.status === RUN_STATUS.FAILED;

  return (
    <Modal isOpen onClose={onClose} title="Sincronizar SKUs con Shopify" size="lg" testId="shopify-sku-sync-modal">
      <div className="space-y-5">
        <p className="text-sm text-text-secondary">
          Trae los productos de la tienda al catálogo de Ninja: crea los que faltan, completa el EAN o el peso, y
          vincula cada variante con su producto. No cambia nada en Shopify. También corre sola cada noche.
        </p>

        {channelOptions.length > 1 && (
          <FilterableSelect
            label="Tienda"
            variant="form"
            value={selectedId ?? ''}
            onChange={(settingId) => dispatch(stockSyncChannelSelected(Number(settingId)))}
            options={channelOptions}
            placeholder="Elige la tienda"
            searchPlaceholder="Buscar cliente o tienda..."
            testId="shopify-store"
          />
        )}

        {selectedId && detailLoading && !detail && (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-4 border-ninja-green border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {selectedId && detailError && (
          <ErrorState
            variant="inline"
            title="No se pudo leer la tienda"
            description={detailError}
            onRetry={() => dispatch(fetchStockSync({ settingId: selectedId }))}
          />
        )}

        {detail && (
          <>
            <p className="text-sm text-text-muted" data-testid="shopify-store-summary">
              {detail.channel.warehouse.name} · {formatQuantity(detail.listings?.total ?? 0)}{' '}
              {detail.listings?.total === 1 ? 'producto vinculado' : 'productos vinculados'}
            </p>
            <ShopifyScopesNotice
              problem={scopesProblemOf(detail)}
              companyName={detail.channel.company.name}
              onReauthorize={handleReauthorize}
              reauthorizing={reauthorizing}
              error={reauthorizeError}
            />
            <ShopifyRunSummary kind={RUN_KIND.CATALOG} report={report} />
          </>
        )}

        {error && (
          <p data-testid="shopify-sku-sync-error" className="text-sm font-semibold text-danger">
            {error}
          </p>
        )}
      </div>

      <div className="flex justify-end gap-3 mt-8">
        <Button variant="ghost" onClick={onClose}>
          Cerrar
        </Button>
        <Button
          variant="primary"
          icon={failed ? RotateCcw : RefreshCw}
          loading={starting || running}
          loadingLabel="Sincronizando…"
          disabled={Boolean(blocker)}
          title={blocker || undefined}
          onClick={handleSync}
          data-testid="shopify-sku-sync-start"
        >
          {failed ? 'Reintentar' : 'Sincronizar SKUs'}
        </Button>
      </div>
    </Modal>
  );
};

/** Mounted only while open: each opening reads the store again. */
export const ShopifySkuSyncModal = ({ isOpen, ...props }) => (isOpen ? <ShopifySkuSyncDialog {...props} /> : null);
